import { useEffect, useState, type FormEvent } from 'react';
import { api, errorText } from '../../api';
import { canShowPdf, PdfPages } from '../../components/Pdf';
import { t, tl, tx } from '../../i18n';
import { fmtDateTime, fmtKB, monthName, MONTHS, today } from '../../lib/format';
import { useToast } from '../../lib/toast';
import { useApi } from '../../lib/useApi';
import { CATEGORY_LABEL, FOLDERS } from './labels';
import { Sheet } from './Sheet';
import type { BelegDetail, Category, YearsResponse } from './types';
import { ShareFile } from '../../components/ShareFile';

const BOOKED_HINT = tl('Dieser Beleg ist schon gebucht. Löschen oder Verschieben bitte über einen Kommentar an Ihre Kanzlei.');
const IMAGE = /^image\/(jpeg|png|gif|webp)$/;
// Kontoauszug-Datei aus dem Online-Banking (CAMT als XML, MT940 als Text, CSV, ZIP): gibt es nur im Ordner „Kontoauszug“;
// eine XML-Datei dort ist keine E-Rechnung
const BANK_MIME = /^(application\/(xml|zip)|text\/(plain|csv))$/;
export const istBankDatei = (b: { category: Category; mime: string }) => b.category === 'kontoauszug' && BANK_MIME.test(b.mime);

type Mode = '' | 'rename' | 'move' | 'delete';

// Beleg ansehen, umbenennen, verschieben, löschen und kommentieren (Fenster über der Ordner-Ansicht)
export function BelegSheet({ id, onClose }: { id: string; onClose(): void }) {
  const toast = useToast();
  const beleg = useApi<BelegDetail>(`/api/app/beleg/${id}`, { live: ['belege'] });
  const [mode, setMode] = useState<Mode>('');
  const b = beleg.data;

  // Kommentare der Kanzlei gelten als gelesen, sobald das Fenster sie zeigt
  const staffComments = b?.comments.filter((c) => c.side === 'staff').length ?? 0;
  useEffect(() => {
    if (staffComments > 0) api.post(`/api/app/beleg/${id}/read`).catch(() => {});
  }, [id, staffComments]);

  if (!b) {
    return (
      <Sheet title={t('Beleg')} onClose={onClose}>
        {beleg.error ? <p className="meta">{beleg.error}</p> : <div className="spinner" aria-label={t('Lädt')} />}
      </Sheet>
    );
  }

  const fileUrl = `/api/app/beleg/${b.id}/file`;
  const where = `${b.clientName} › ${b.year} › ${monthName(b.month)} › ${t(CATEGORY_LABEL[b.category])}`;

  return (
    <Sheet title={b.name} onClose={onClose}>
      <Preview url={fileUrl} mime={b.mime} name={b.name} bank={istBankDatei(b)} />
      <div className="meta">
        {where} · {fmtKB(b.size)}
      </div>
      {mode === 'rename' && <RenameForm b={b} done={() => setMode('')} reload={beleg.reload} />}
      {mode === 'move' && <MoveForm b={b} cancel={() => setMode('')} moved={onClose} />}
      {mode === 'delete' && <DeleteConfirm b={b} cancel={() => setMode('')} deleted={onClose} />}
      {mode === '' && (
        <>
          <div className="row">
            <button type="button" className="btn2" onClick={() => setMode('rename')}>
              {t('Umbenennen')}
            </button>
            {!b.locked && (
              <button type="button" className="btn2" onClick={() => setMode('move')}>
                {t('Verschieben')}
              </button>
            )}
            {!b.locked && (
              <button type="button" className="btn2 del" onClick={() => setMode('delete')}>
                {t('Löschen')}
              </button>
            )}
            <a className="btn2" href={`${fileUrl}?download=1`} download>
              {t('Herunterladen')}
            </a>
            <ShareFile url={`${fileUrl}?download=1`} name={b.name} />
          </div>
          {b.locked && <div className="meta">{t(BOOKED_HINT)}</div>}
          <Comments b={b} reload={beleg.reload} />
        </>
      )}
    </Sheet>
  );
}

function RenameForm({ b, done, reload }: { b: BelegDetail; done(): void; reload(): Promise<void> }) {
  const toast = useToast();
  const dot = b.name.lastIndexOf('.');
  const [value, setValue] = useState(dot > 0 ? b.name.slice(0, dot) : b.name);
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!value.trim()) return;
    setBusy(true);
    try {
      await api.post(`/api/app/beleg/${b.id}/rename`, { name: value });
      await reload();
      toast(t('Umbenannt'));
      done();
    } catch (err) {
      toast(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="row" onSubmit={submit}>
      <input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        aria-label={t('Neuer Name')}
        maxLength={200}
        autoFocus
        onFocus={(e) => e.target.select()}
      />
      <button className="btn2 dark" type="submit" disabled={busy}>
        {t('Speichern')}
      </button>
      <button className="btn2" type="button" onClick={done}>
        {t('Abbrechen')}
      </button>
    </form>
  );
}

// bank: Kontoauszug-Datei aus dem Online-Banking (sonst gilt jede XML-Datei als E-Rechnung)
export function Preview({ url, mime, name, bank = false }: { url: string; mime: string; name: string; bank?: boolean }) {
  const [failed, setFailed] = useState(false);
  const pdf = mime === 'application/pdf';
  const none = (
    <div className="noprev">
      <span>
        {bank
          ? t('Bankdatei (Kontoauszug), keine Vorschau')
          : mime === 'application/xml'
            ? t('E-Rechnung (XML), keine Vorschau')
            : t('Vorschau nicht verfügbar')}
      </span>
      <a className="btn2" href={`${url}?download=1`} download>
        {t('Herunterladen')}
      </a>
    </div>
  );
  return (
    <div className="preview">
      {failed || (!IMAGE.test(mime) && !pdf) ? (
        none
      ) : pdf && canShowPdf() ? (
        <iframe src={url} title={name} />
      ) : pdf ? (
        <div className="pdfbox">
          <PdfPages url={url} title={name} fallback={none} />
        </div>
      ) : (
        <img src={url} alt={name} onError={() => setFailed(true)} />
      )}
    </div>
  );
}

function MoveForm({ b, cancel, moved }: { b: BelegDetail; cancel(): void; moved(): void }) {
  const toast = useToast();
  const years = useApi<YearsResponse>(`/api/app/belege/${b.clientId}/years`);
  const [year, setYear] = useState(b.year);
  const [month, setMonth] = useState(b.month);
  const [category, setCategory] = useState<Category>(b.category);
  const [busy, setBusy] = useState(false);
  const now = today();
  const list = years.data?.years.map((y) => y.year) ?? [b.year];

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (year * 12 + month > now.year * 12 + now.month) {
      toast(t('Bitte einen Monat bis {month} {year} wählen', { month: monthName(now.month), year: now.year }));
      return;
    }
    setBusy(true);
    try {
      // derselbe Ort: gleiche Meldung wie in der Demo, nur ändert sich nichts
      await api.post<{ moved: boolean }>(`/api/app/beleg/${b.id}/move`, { year, month, category });
      toast(t('Verschoben nach {where}', { where: `${year} › ${monthName(month)} › ${t(CATEGORY_LABEL[category])}` }));
      moved();
    } catch (err) {
      toast(errorText(err));
      setBusy(false);
    }
  };

  return (
    <form className="moveform" onSubmit={submit}>
      <b>{t('Wohin gehört der Beleg?')}</b>
      <div className="row">
        <select aria-label={t('Jahr')} value={year} onChange={(e) => setYear(Number(e.target.value))}>
          {list.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
        <select aria-label={t('Monat')} value={month} onChange={(e) => setMonth(Number(e.target.value))}>
          {MONTHS.map((m, i) => (
            <option key={m} value={i + 1}>
              {monthName(i + 1)}
            </option>
          ))}
        </select>
        <select
          aria-label={t('Ordner')}
          value={category}
          onChange={(e) => setCategory(e.target.value as Category)}
          className="full"
        >
          {FOLDERS.map((f) => (
            <option key={f.key} value={f.key}>
              {t(f.label)}
            </option>
          ))}
        </select>
      </div>
      <div className="row">
        <button className="btn2 dark" type="submit" disabled={busy}>
          {t('Hierhin verschieben')}
        </button>
        <button className="btn2" type="button" onClick={cancel}>
          {t('Abbrechen')}
        </button>
      </div>
    </form>
  );
}

function DeleteConfirm({ b, cancel, deleted }: { b: BelegDetail; cancel(): void; deleted(): void }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const really = async () => {
    setBusy(true);
    try {
      await api.del(`/api/app/beleg/${b.id}`);
      toast(t('Gelöscht: {name}', { name: b.name }));
      deleted();
    } catch (err) {
      toast(errorText(err));
      setBusy(false);
    }
  };
  return (
    <div className="confirm" role="alertdialog" aria-label={t('Beleg löschen')}>
      {t('Diesen Beleg wirklich löschen? Die Kanzlei sieht ihn danach nicht mehr.')}
      <div className="row">
        <button type="button" className="btn2 del solid" onClick={really} disabled={busy} autoFocus>
          {t('Ja, löschen')}
        </button>
        <button type="button" className="btn2" onClick={cancel}>
          {t('Abbrechen')}
        </button>
      </div>
    </div>
  );
}

function Comments({ b, reload }: { b: BelegDetail; reload(): Promise<void> }) {
  const toast = useToast();
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const body = text.trim();
    if (!body) return;
    setBusy(true);
    try {
      await api.post(`/api/app/beleg/${b.id}/comments`, { body });
      setText('');
      await reload();
      toast(t('Kommentar an Ihre Kanzlei gesendet'));
    } catch (err) {
      toast(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="rthread">
      <div className="rt-h">{t('Kommentare zum Beleg')}</div>
      {b.comments.map((c) => (
        <div key={c.id} className={`rc${c.mine ? ' rc-me' : ''}`}>
          <b>{c.mine ? t('Sie') : tx(c.authorName)}</b> <span>{fmtDateTime(c.createdAt)}</span>
          <div>{c.body}</div>
        </div>
      ))}
      <form className="rc-form" onSubmit={submit}>
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          maxLength={1000}
          placeholder={t('Kommentar an Ihre Kanzlei')}
          aria-label={t('Kommentar zu {name}', { name: b.name })}
        />
        <button type="submit" className="btn2" disabled={busy}>
          {t('Senden')}
        </button>
      </form>
    </div>
  );
}
